import { createContext, useEffect, useState } from "react";
import BreadCrumb from "../../../Components/BreadCrumb";
import { CheckCircle, Palette, Target, User2Icon, Download } from "lucide-react";
import { MultiStep } from "../../../Components/MultiStep";
import { ToastMessage } from "../../../Components/Utils";
import ClientDetailForm from "./Forms/ClientDetailForm";
import CampaignObjectiveForm from "./Forms/CampaignObjectiveForm";
import CreativePrefencesForm from "./Forms/CreativePrefencesForm";
import ReviewSubmit from "./Forms/ReviewSubmit";
import ResponseDisplay from "./Forms/ResponseDisplay";
import { initialFormData } from "./Components/initialValue";
import { validateCurrentStep } from "./Forms/ErrorHandling";
import AxiosInstance from "../../../Components/AxiosInstance";
import { campaigns_api_url } from "../../../Components/ApiURLs";

const steps = [
  {
    name: "Patient Type",
    icon: User2Icon
  },
  {
    name: "Campaign objective",
    icon: Target
  },
  {
    name: "Creative preferences",
    icon: Palette
  },
  {
    name: "Generate and Review",
    icon: CheckCircle
  },
  {
    name: "Save",
    icon: Download
  }
];

export const CampaignFormContext = createContext();

const CampaignForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseData, setResponseData] = useState(null);

  const handleNext = () => {
    const validation = validateCurrentStep(formData, currentStep);
    if (!validation.valid) {
      ToastMessage({
        type: "error",
        title: "Validation Error",
        message: validation.message
      });
      return;
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const axios = AxiosInstance();
      await axios.post(campaigns_api_url, formData).then((res) => {
        ToastMessage({
          type: "success",
          title: "AI Brief Generated Successfully",
          message: "Campaign Brief has been generated."
        });
        setResponseData(res.data || res);
        setCurrentStep(4);
        console.log(res);
        setIsSubmitting(false);
      }).catch((err) => {
        ToastMessage({
          type: "error",
          title: "Submission Failed",
          message: err?.response?.data?.message || "An error occurred while submitting the campaign."
        });
        console.error(err);
        setIsSubmitting(false);
      });
    } catch (error) {
      ToastMessage({
        type: "error",
        title: "Submission Failed",
        message: error?.response?.data?.message || "An error occurred while submitting the campaign."
      });
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <BreadCrumb
        previous={"Campaign"}
        previousLink="/campaigns"
        title={"Create AI-Assisted Campaigns"}
      />
      <div className="px-6 py-6">
        {/* Stepper */}
        <MultiStep handleNext={handleNext} currentStep={currentStep} setCurrentStep={setCurrentStep} steps={steps} />

        {/* Content Area */}
        <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-sm border dark:border-gray-600">
          <h2 className="flex items-center gap-3 border-b-4 border-green-500 pb-2 mb-4">
            <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Step {currentStep + 1}: {steps[currentStep]?.name}
            </span>
          </h2>
          <CampaignFormContext.Provider value={{ setFormData, formData, handleSubmit, isSubmitting, responseData }}>
            {currentStep === 0 && (
              <ClientDetailForm />
            )}
            {currentStep === 1 && (
              <CampaignObjectiveForm />
            )}
            {currentStep === 2 && (
              <CreativePrefencesForm />
            )}
            {currentStep === 3 && (
              <ReviewSubmit />
            )}
            {currentStep === 4 && (
              <ResponseDisplay />
            )}
          </CampaignFormContext.Provider>
          {/* Navigation */}
          <div className="flex justify-between mt-6">
            <button
              disabled={currentStep === 0}
              onClick={() => setCurrentStep((prev) => prev - 1)}
              className="px-4 py-2 rounded-lg border text-gray-600 disabled:opacity-40"
            >
              Back
            </button>

            <div>
              <button
                disabled={currentStep === 3}
                onClick={handleNext}
                className="px-4 py-2 rounded-lg bg-green-500 text-white disabled:opacity-40 hover:bg-green-600 transition"
              >
                {currentStep === 4 ? 'Save' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CampaignForm;

